import {
    createContext,
    lazy,
    type ReactNode,
    Suspense,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';

import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { type Mode } from '@libraries/typescript/api_client';

export type AttachmentViewerTarget = {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
};

type OpenAttachmentViewer = (target: AttachmentViewerTarget) => void;

const AttachmentViewerContext = createContext<OpenAttachmentViewer | null>(
    null,
);

// The provider wraps the whole app, so the viewer's markdown, table and pdf
// code loads on first open instead of with the app shell.
const AttachmentViewer = lazy(async () => {
    const bodies =
        await import('@apps/web/features/phoebe_chat_v3/ui/attachment_viewer_bodies');
    return { default: bodies.AttachmentViewer };
});

function openWithoutProvider(): never {
    throw new Error(
        'useOpenAttachmentViewer requires an AttachmentViewerProvider.',
    );
}

export function useOpenAttachmentViewer(): OpenAttachmentViewer {
    return useContext(AttachmentViewerContext) ?? openWithoutProvider;
}

export function AttachmentViewerProvider({
    children,
}: {
    readonly children: ReactNode;
}) {
    const [state, setState] = useState<{
        readonly target: AttachmentViewerTarget | null;
        readonly open: boolean;
    }>({ target: null, open: false });
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const openViewer = useCallback((target: AttachmentViewerTarget) => {
        returnFocusRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        setState({ target, open: true });
    }, []);
    return (
        <AttachmentViewerContext.Provider value={openViewer}>
            {children}
            <Dialog
                open={state.open}
                onOpenChange={(open) =>
                    setState((current) => ({ ...current, open }))
                }
            >
                {state.target !== null && (
                    <DialogContent
                        showCloseButton={false}
                        // Radix only restores focus to its own trigger, and
                        // cards open the viewer through context instead.
                        onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            returnFocusRef.current?.focus();
                        }}
                        className="flex h-[calc(100dvh-3rem)] w-[calc(100vw-3rem)] max-w-none flex-col gap-0 overflow-hidden p-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:overflow-hidden max-sm:rounded-none max-sm:border-0 max-sm:p-0 sm:max-w-none"
                    >
                        <Suspense
                            fallback={
                                <DialogTitle className="sr-only">
                                    File preview
                                </DialogTitle>
                            }
                        >
                            <AttachmentViewer
                                key={state.target.attachmentId}
                                target={state.target}
                            />
                        </Suspense>
                    </DialogContent>
                )}
            </Dialog>
        </AttachmentViewerContext.Provider>
    );
}
